import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTasks, useGenerateTasks, usePerformanceKPI, useCriticalPath } from '../hooks/useTasks'
import { useProject } from '../hooks/useProject'
import { useCurvaS } from '../hooks/useCurvaS'
import { GanttChart } from '../components/charts/GanttChart'
import { TaskCard } from '../components/cards/TaskCard'
import { PerformanceKPICard } from '../components/cards/PerformanceKPICard'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/Tabs'
import { LoadingSkeleton } from '../components/common/LoadingSkeleton'
import { apiClient } from '../lib/api-client'

const TasksPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const numericProjectId = projectId ? parseInt(projectId) : 0
  
  const [activeTab, setActiveTab] = useState('gantt')
  const [filterStatus, setFilterStatus] = useState<'all' | 'planned' | 'in_progress' | 'completed' | 'delayed'>('all')
  
  const { data: project, isLoading: isLoadingProject } = useProject(numericProjectId)
  const { data: tasksData, isLoading: isLoadingTasks, refetch: refetchTasks } = useTasks(numericProjectId)
  const { mutate: generateTasks, isPending: isGenerating } = useGenerateTasks(numericProjectId)
  const { data: criticalPathData, isLoading: isLoadingCriticalPath } = useCriticalPath(numericProjectId)
  const { data: curvaData, isLoading: isLoadingCurva } = useCurvaS(numericProjectId)
  
  useEffect(() => {
    if (!projectId) {
      navigate('/projects')
    }
  }, [projectId, navigate])
  
  const handleGenerateTasks = () => {
    generateTasks({ deleteExisting: true }, {
      onSuccess: () => {
        refetchTasks()
      }
    })
  }
  
  const filteredTasks = tasksData?.tasks.filter((task) => {
    if (filterStatus === 'all') return true
    return task.status === filterStatus
  }) || []
  
  const criticalPathIds = criticalPathData?.criticalPathIds || []
  
  if (isLoadingProject || isLoadingTasks || isLoadingCriticalPath || isLoadingCurva) {
    return <LoadingSkeleton />
  }
  
  if (!project) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <p className="text-gray-500">Project not found</p>
        </div>
      </div>
    )
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
            <p className="text-gray-600 mt-1">Task Management & Timeline</p>
          </div>
          
          <div className="flex items-center gap-4">
            <button
              onClick={handleGenerateTasks}
              disabled={isGenerating}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              {isGenerating ? 'Generating...' : 'Generate from BoQ'}
            </button>
            <button
              onClick={() => refetchTasks()}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        </div>
        
        {tasksData && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-gray-600">Total Tasks</p>
              <p className="font-semibold text-gray-900">{tasksData.totalTasks}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-gray-600">Completed</p>
              <p className="font-semibold text-gray-900">{tasksData.completedTasks}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-gray-600">In Progress</p>
              <p className="font-semibold text-gray-900">{tasksData.inProgressTasks}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-gray-600">Delayed</p>
              <p className="font-semibold text-gray-900">{tasksData.delayedTasks}</p>
            </div>
          </div>
        )}
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="gantt">Gantt Chart</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="resources">Resources</TabsTrigger>
          <TabsTrigger value="kpi">KPI</TabsTrigger>
        </TabsList>
        
        <TabsContent value="gantt">
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm mt-4">
            <GanttChart
              tasks={filteredTasks}
              criticalPath={criticalPathIds}
              height={600}
            />
          </div>
        </TabsContent>
        
        <TabsContent value="timeline">
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm mt-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setFilterStatus('all')}
                  className={`px-3 py-1 rounded-md text-sm ${filterStatus === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilterStatus('planned')}
                  className={`px-3 py-1 rounded-md text-sm ${filterStatus === 'planned' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                >
                  Planned
                </button>
                <button
                  onClick={() => setFilterStatus('in_progress')}
                  className={`px-3 py-1 rounded-md text-sm ${filterStatus === 'in_progress' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                >
                  In Progress
                </button>
                <button
                  onClick={() => setFilterStatus('completed')}
                  className={`px-3 py-1 rounded-md text-sm ${filterStatus === 'completed' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                >
                  Completed
                </button>
                <button
                  onClick={() => setFilterStatus('delayed')}
                  className={`px-3 py-1 rounded-md text-sm ${filterStatus === 'delayed' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                >
                  Delayed
                </button>
              </div>
            </div>
            
            <div className="space-y-4">
              {filteredTasks.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No tasks found</p>
              ) : (
                filteredTasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    projectId={numericProjectId}
                    task={task}
                    onStatusChange={refetchTasks}
                  />
                ))
              )}
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="resources">
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm mt-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Resource Allocation</h2>
            <p className="text-gray-600 mb-4">Resource allocation dashboard will be implemented here</p>
            <p className="text-sm text-gray-500">This tab will show resource distribution per period based on Curva S data</p>
          </div>
        </TabsContent>
        
        <TabsContent value="kpi">
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm mt-4">
            <PerformanceKPICard projectId={numericProjectId} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}

TasksPage.displayName = 'TasksPage'
export default TasksPage